import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Plus, Edit2, Trash2, Loader2, X, CheckCircle, Clock, Play, ArrowUp, ArrowDown, Settings, History, List, Users, Share2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import ConditionBuilder, { Condition } from '../components/ConditionBuilder';

interface WorkflowStep {
  id?: number;
  workflowId?: number;
  stepOrder?: number;
  stepType: 'condition' | 'delay' | 'action';
  config: any;
  isActive?: number | boolean;
}

interface Workflow {
  id?: number;
  key: string;
  name: string;
  description?: string;
  triggerType: 'event' | 'schedule';
  triggerConfig: { eventType?: string; [k: string]: any };
  targetType: 'customer' | 'referrer';
  isActive: number | boolean;
  maxRunsPerCustomer: number;
  steps: WorkflowStep[];
  createdAt?: number;
  updatedAt?: number;
}

interface WorkflowExecution {
  id: number;
  workflowId: number;
  customerPhone: string;
  triggerEvent: string;
  triggerData: any;
  status: string;
  startedAt: number;
  completedAt?: number;
  stepCount: number;
}

interface WorkflowExecutionStep {
  id: number;
  executionId: number;
  workflowStepId: number;
  status: string;
  output: any;
  executedAt?: number;
}

function toBool(v: number | boolean | undefined): boolean {
  return v === 1 || v === true;
}

function stepTypeLabel(type: string): string {
  if (type === 'condition') return '條件';
  if (type === 'delay') return '延遲';
  if (type === 'action') return '動作';
  return type;
}

function stepTypeBadgeClass(type: string): string {
  if (type === 'condition') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300';
  if (type === 'delay') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300';
  if (type === 'action') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
}

function defaultStepConfig(type: WorkflowStep['stepType']): any {
  if (type === 'condition') return { conditions: [] as Condition[] };
  if (type === 'delay') return { days: 0, hours: 0, minutes: 0 };
  return { type: 'send_whatsapp', template_key: '', message: '', variables: '{}' };
}

const AdminCustomerJourneys: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const token = localStorage.getItem('admin_token');

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filterTarget, setFilterTarget] = useState<'all' | 'customer' | 'referrer'>('all');

  const [showLogs, setShowLogs] = useState(false);
  const [logWorkflow, setLogWorkflow] = useState<Workflow | null>(null);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<WorkflowExecution | null>(null);
  const [executionSteps, setExecutionSteps] = useState<WorkflowExecutionStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>(undefined);

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<'event' | 'schedule'>('event');
  const [triggerEvent, setTriggerEvent] = useState('');
  const [targetType, setTargetType] = useState<'customer' | 'referrer'>('customer');
  const [maxRuns, setMaxRuns] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<WorkflowStep[]>([]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadWorkflows();
  }, [token, navigate]);

  const loadWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/workflows');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取旅程失敗');
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取旅程失敗');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setKey('');
    setName('');
    setDescription('');
    setTriggerType('event');
    setTriggerEvent('');
    setTargetType('customer');
    setMaxRuns(0);
    setIsActive(true);
    setSteps([]);
  };

  const openCreate = () => {
    setEditingId(undefined);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (wf: Workflow) => {
    setEditingId(wf.id);
    setKey(wf.key || '');
    setName(wf.name || '');
    setDescription(wf.description || '');
    setTriggerType(wf.triggerType || 'event');
    setTriggerEvent(wf.triggerConfig?.eventType || '');
    setTargetType(wf.targetType || 'customer');
    setMaxRuns(wf.maxRunsPerCustomer ?? 0);
    setIsActive(toBool(wf.isActive));
    setSteps(
      Array.isArray(wf.steps)
        ? wf.steps.map((s) => ({
            ...s,
            config: s.config ? { ...s.config } : defaultStepConfig(s.stepType),
          }))
        : []
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(undefined);
    resetForm();
  };

  const addStep = (type: WorkflowStep['stepType']) => {
    setSteps((prev) => [...prev, { stepType: type, config: defaultStepConfig(type), isActive: true }]);
  };

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateStepConfig = (index: number, config: any) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, config } : s)));
  };

  const parseVariables = (raw: string): object => {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE)) {
      setError('沒有權限');
      return;
    }
    if (!key.trim() || !name.trim()) {
      setError('請填寫旅程代碼與名稱');
      return;
    }
    if (triggerType === 'event' && !triggerEvent.trim()) {
      setError('請填寫觸發事件');
      return;
    }

    const triggerConfig = triggerType === 'event' ? { eventType: triggerEvent.trim() } : {};

    const payloadSteps = steps.map((s) => {
      let config = s.config;
      if (s.stepType === 'action' && config.type === 'send_whatsapp' && typeof config.variables === 'string') {
        config = { ...config, variables: parseVariables(config.variables) };
      }
      return {
        stepType: s.stepType,
        config,
        isActive: toBool(s.isActive),
      };
    });

    const payload = {
      key: key.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      triggerType,
      triggerConfig,
      targetType,
      maxRunsPerCustomer: Number(maxRuns) || 0,
      isActive,
      steps: payloadSteps,
    };

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const url = editingId ? `/api/public/admin/workflows/${editingId}` : '/api/public/admin/workflows';
      const method = editingId ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess(editingId ? '旅程已更新' : '旅程已建立');
      closeModal();
      loadWorkflows();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!confirm('確定要刪除這個客戶旅程嗎？')) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/workflows/${id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      setSuccess('旅程已刪除');
      loadWorkflows();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleSeed = async () => {
    if (!confirm('這會建立預設的客戶旅程（若資料庫已有旅程則不會重複建立）。確定繼續嗎？')) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/workflows/seed', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '種子失敗');
      setSuccess(`已建立 ${data.count || 0} 個預設旅程`);
      loadWorkflows();
    } catch (e: any) {
      setError(e.message || '種子失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const openLogs = async (wf: Workflow) => {
    setLogWorkflow(wf);
    setShowLogs(true);
    setExecutions([]);
    setSelectedExecution(null);
    setExecutionSteps([]);
    setLoadingLogs(true);
    try {
      const res = await apiFetch(`/api/public/admin/workflows/${wf.id}/executions`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取執行記錄失敗');
      setExecutions(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取執行記錄失敗');
    } finally {
      setLoadingLogs(false);
    }
  };

  const closeLogs = () => {
    setShowLogs(false);
    setLogWorkflow(null);
    setExecutions([]);
    setSelectedExecution(null);
    setExecutionSteps([]);
  };

  const loadExecutionSteps = async (exec: WorkflowExecution) => {
    setSelectedExecution(exec);
    setLoadingSteps(true);
    setExecutionSteps([]);
    try {
      const res = await apiFetch(`/api/public/admin/workflow-executions/${exec.id}/steps`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取步驟詳情失敗');
      setExecutionSteps(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取步驟詳情失敗');
    } finally {
      setLoadingSteps(false);
    }
  };

  const formatTime = (ts?: number) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-HK');
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300';
      case 'running':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const filteredWorkflows = workflows.filter((wf) =>
    filterTarget === 'all' ? true : wf.targetType === filterTarget
  );

  return (
    <AdminLayout currentPage="customer-journeys" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Route className="w-6 h-6 text-orange-600" />
          客戶旅程
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">事件驅動的自動化流程：自動標籤、發送訊息、延遲任務</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Target filter tabs */}
      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4" aria-label="Tabs">
          {[
            { key: 'all', label: '全部旅程', icon: Route },
            { key: 'customer', label: '客戶旅程', icon: Users },
            { key: 'referrer', label: '推薦人旅程', icon: Share2 },
          ].map((tab) => {
            const active = filterTarget === (tab.key as any);
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setFilterTarget(tab.key as any)}
                className={`
                  inline-flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors
                  ${active
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Actions */}
      <div className="mb-4 flex flex-wrap gap-2 justify-end">
        {can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE) && (
          <>
            <button
              onClick={handleSeed}
              disabled={saving}
              className="inline-flex items-center gap-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <Settings className="w-4 h-4" /> 載入預設旅程
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> 新增旅程
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">旅程</th>
                <th className="p-4">觸發類型</th>
                <th className="p-4">目標</th>
                <th className="p-4">步驟數</th>
                <th className="p-4">狀態</th>
                <th className="p-4 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> 加載中...
                  </td>
                </tr>
              ) : filteredWorkflows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無客戶旅程，可先載入預設旅程
                  </td>
                </tr>
              ) : (
                filteredWorkflows.map((wf) => (
                  <tr key={wf.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-gray-800 dark:text-white">{wf.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{wf.key}</div>
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">
                      {wf.triggerType === 'event' ? (
                        <span className="inline-flex items-center gap-1"><Play size={14} /> 事件：{wf.triggerConfig?.eventType || '-'}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1"><Clock size={14} /> 排程</span>
                      )}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{wf.targetType === 'customer' ? '客戶' : '推薦人'}</td>
                    <td className="p-4 text-gray-800 dark:text-white">{Array.isArray(wf.steps) ? wf.steps.length : 0}</td>
                    <td className="p-4">
                      {toBool(wf.isActive) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300">啟用</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">停用</span>
                      )}
                    </td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => openLogs(wf)}
                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-xs"
                      >
                        <History size={14} /> 日誌
                      </button>
                      {can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE) && (
                        <>
                          <button
                            onClick={() => openEdit(wf)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                          >
                            <Edit2 size={14} /> 編輯
                          </button>
                          <button
                            onClick={() => handleDelete(wf.id)}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                          >
                            <Trash2 size={14} /> 刪除
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingId ? '編輯客戶旅程' : '新增客戶旅程'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6">
              {/* Header */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">旅程代碼 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    placeholder="例如：first_order_thanks"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">旅程名稱 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="例如：首次下單感謝"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="簡短說明旅程用途"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">觸發類型</label>
                  <select
                    value={triggerType}
                    onChange={(e) => setTriggerType(e.target.value as 'event' | 'schedule')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="event">事件</option>
                    <option value="schedule">排程</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">目標類型</label>
                  <select
                    value={targetType}
                    onChange={(e) => setTargetType(e.target.value as 'customer' | 'referrer')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="customer">客戶</option>
                    <option value="referrer">推薦人</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">每人最多執行次數（0 = 無限）</label>
                  <input
                    type="number"
                    min={0}
                    value={maxRuns}
                    onChange={(e) => setMaxRuns(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              {triggerType === 'event' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">觸發事件 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={triggerEvent}
                    onChange={(e) => setTriggerEvent(e.target.value)}
                    placeholder="例如：order_created, payment_confirmed, order_completed, referral_made, begin_checkout"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">常用事件：order_created、payment_confirmed、order_completed、referral_made、referral_milestone、begin_checkout</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  id="wf-is-active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="wf-is-active" className="text-sm font-medium text-gray-700 dark:text-gray-300">啟用旅程</label>
              </div>

              {/* Steps */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">步驟編輯器</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => addStep('condition')}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      <Plus size={14} /> 條件
                    </button>
                    <button
                      type="button"
                      onClick={() => addStep('delay')}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300"
                    >
                      <Plus size={14} /> 延遲
                    </button>
                    <button
                      type="button"
                      onClick={() => addStep('action')}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      <Plus size={14} /> 動作
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {steps.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">尚未添加步驟</div>
                  )}
                  {steps.map((step, idx) => (
                    <StepEditor
                      key={`${idx}-${step.stepType}`}
                      index={idx}
                      step={step}
                      targetType={targetType}
                      onChange={(updated) => updateStepConfig(idx, updated.config)}
                      onRemove={() => removeStep(idx)}
                      onMove={(dir) => moveStep(idx, dir)}
                      isFirst={idx === 0}
                      isLast={idx === steps.length - 1}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-gray-800 pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? '儲存變更' : '建立旅程'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogs && logWorkflow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <History className="w-5 h-5 text-orange-600" />
                執行日誌：{logWorkflow.name}
              </h2>
              <button onClick={closeLogs} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {loadingLogs ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> 加載中...
                </div>
              ) : executions.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">暫無執行記錄</div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        <tr>
                          <th className="p-3">電話</th>
                          <th className="p-3">觸發事件</th>
                          <th className="p-3">狀態</th>
                          <th className="p-3">開始時間</th>
                          <th className="p-3">完成時間</th>
                          <th className="p-3">步驟</th>
                          <th className="p-3 text-right">詳情</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {executions.map((exec) => (
                          <tr
                            key={exec.id}
                            className={`hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors ${selectedExecution?.id === exec.id ? 'bg-orange-50 dark:bg-gray-700/50' : ''}`}
                          >
                            <td className="p-3 text-gray-800 dark:text-white font-mono">{exec.customerPhone}</td>
                            <td className="p-3 text-gray-600 dark:text-gray-300">{exec.triggerEvent}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(exec.status)}`}>
                                {exec.status}
                              </span>
                            </td>
                            <td className="p-3 text-gray-600 dark:text-gray-300">{formatTime(exec.startedAt)}</td>
                            <td className="p-3 text-gray-600 dark:text-gray-300">{formatTime(exec.completedAt)}</td>
                            <td className="p-3 text-gray-800 dark:text-white">{exec.stepCount}</td>
                            <td className="p-3 text-right">
                              <button
                                onClick={() => loadExecutionSteps(exec)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                              >
                                <List size={14} /> 查看
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {selectedExecution && (
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">
                        執行 #{selectedExecution.id} 步驟詳情
                      </h3>
                      {loadingSteps ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> 加載中...
                        </div>
                      ) : executionSteps.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400">暫無步驟記錄</div>
                      ) : (
                        <div className="space-y-2">
                          {executionSteps.map((step, idx) => (
                            <div
                              key={step.id}
                              className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                            >
                              <span className="text-xs text-gray-400 mt-0.5">{idx + 1}</span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(step.status)}`}>
                                    {step.status}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatTime(step.executedAt)}
                                  </span>
                                </div>
                                {step.output && (
                                  <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                                    {JSON.stringify(step.output, null, 2)}
                                  </pre>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeLogs}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

interface StepEditorProps {
  index: number;
  step: WorkflowStep;
  targetType: 'customer' | 'referrer';
  onChange: (step: WorkflowStep) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
}

const StepEditor: React.FC<StepEditorProps> = ({ index, step, targetType, onChange, onRemove, onMove, isFirst, isLast }) => {
  const config = step.config || {};
  const [giftOptions, setGiftOptions] = useState<{ id: number; key: string; name: string }[]>([]);

  useEffect(() => {
    if (step.stepType !== 'action' || config.type !== 'add_gift') return;
    let cancelled = false;
    apiFetch('/api/public/admin/gift-catalog')
      .then(res => (res.ok ? res.json() : []))
      .then((data: any[]) => {
        if (!cancelled && Array.isArray(data)) {
          setGiftOptions(data.filter((g: any) => g.isActive));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [step.stepType, config.type]);

  const updateConfig = (patch: any) => {
    onChange({ ...step, config: { ...config, ...patch } });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stepTypeBadgeClass(step.stepType)}`}>
            {stepTypeLabel(step.stepType)}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">步驟 {index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={isFirst}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
            title="上移"
          >
            <ArrowUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={isLast}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
            title="下移"
          >
            <ArrowDown size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            title="移除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {step.stepType === 'condition' && (
        <ConditionBuilder
          conditions={config.conditions || []}
          onChange={(conditions) => updateConfig({ conditions })}
          targetType={targetType}
        />
      )}

      {step.stepType === 'delay' && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">天</label>
            <input
              type="number"
              min={0}
              value={config.days ?? 0}
              onChange={(e) => updateConfig({ days: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">小時</label>
            <input
              type="number"
              min={0}
              value={config.hours ?? 0}
              onChange={(e) => updateConfig({ hours: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">分鐘</label>
            <input
              type="number"
              min={0}
              value={config.minutes ?? 0}
              onChange={(e) => updateConfig({ minutes: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>
      )}

      {step.stepType === 'action' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">動作類型</label>
            <select
              value={config.type || 'send_whatsapp'}
              onChange={(e) => {
                const type = e.target.value;
                if (type === 'send_whatsapp') {
                  updateConfig({ type, template_key: '', message: '', variables: '{}' });
                } else if (type === 'add_gift') {
                  updateConfig({ type, giftKey: '' });
                } else if (type === 'add_voucher') {
                  updateConfig({ type, amount: 30, validityDays: 15, sourceEvent: 'workflow_voucher' });
                } else {
                  updateConfig({ type, tag: '', color: 'gray' });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="send_whatsapp">發送 WhatsApp</option>
              <option value="add_tag">添加標籤</option>
              <option value="remove_tag">移除標籤</option>
              <option value="add_gift">贈送隨單禮品</option>
              <option value="add_voucher">發放回贈券</option>
            </select>
          </div>

          {config.type === 'send_whatsapp' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">模板代碼（可選）</label>
                <input
                  type="text"
                  value={config.template_key || ''}
                  onChange={(e) => updateConfig({ template_key: e.target.value })}
                  placeholder="例如：order_received_v1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">變數對應 JSON（可選）</label>
                <textarea
                  value={config.variables || '{}'}
                  onChange={(e) => updateConfig({ variables: e.target.value })}
                  placeholder='{"1":"event.orderNum","2":"customer.name"}'
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">鍵為變數索引，值為上下文路徑（如 event.orderNum、customer.name）</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">備用訊息（未使用模板時發送）</label>
                <textarea
                  value={config.message || ''}
                  onChange={(e) => updateConfig({ message: e.target.value })}
                  placeholder="你好 {{name}}，多謝支持好餸社企！"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none"
                />
              </div>
            </>
          )}

          {config.type === 'add_gift' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">選擇禮品</label>
              {giftOptions.length === 0 ? (
                <p className="text-xs text-amber-600 dark:text-amber-400">尚未建立啟用中的禮品，請先到「禮品目錄」新增。</p>
              ) : (
                <select
                  value={config.giftKey || ''}
                  onChange={(e) => updateConfig({ giftKey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="">請選擇禮品</option>
                  {giftOptions.map(g => (
                    <option key={g.id} value={g.key}>{g.name}（{g.key}）</option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">禮品會掛在客戶名下，於下次落單時自動隨單送出。後續 WhatsApp 步驟可用 {'{{giftName}}'} 引用禮品名稱。</p>
            </div>
          )}

          {config.type === 'add_voucher' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">回贈券金額（HKD）</label>
                <input
                  type="number"
                  value={config.amount || 30}
                  onChange={(e) => updateConfig({ amount: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">有效期（天）</label>
                <input
                  type="number"
                  value={config.validityDays || 15}
                  onChange={(e) => updateConfig({ validityDays: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">來源事件標記</label>
                <input
                  type="text"
                  value={config.sourceEvent || 'workflow_voucher'}
                  onChange={(e) => updateConfig({ sourceEvent: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          )}

          {(config.type === 'add_tag' || config.type === 'remove_tag') && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">標籤名稱</label>
                <input
                  type="text"
                  value={config.tag || ''}
                  onChange={(e) => updateConfig({ tag: e.target.value })}
                  placeholder="例如：high-value"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">標籤顏色</label>
                <select
                  value={config.color || 'gray'}
                  onChange={(e) => updateConfig({ color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="gray">灰色</option>
                  <option value="blue">藍色</option>
                  <option value="green">綠色</option>
                  <option value="yellow">黃色</option>
                  <option value="red">紅色</option>
                  <option value="purple">紫色</option>
                  <option value="orange">橙色</option>
                </select>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomerJourneys;
